<template>
  <div>
    <h1>
      {{ $t("files") }}
    </h1>
    <ProgressBar v-if="isLoading" mode="indeterminate" class="mb-4" />
    <div v-else class="grid">
      <!-- Tabela -->
      <div class="col-12">
        <div class="">
          <TreeTable
            :value="files"
            :expanded-keys="expandedKeys"
            :filters="filters"
            class="p-treetable-sm mb-5"
            sort-mode="single"
            filter-mode="lenient"
            responsive-layout="scroll"
            resizable-columns
            column-resize-mode="expand"
          >
            <Column field="name" :header="$t('name')" expander sortable>
              <template #filter>
                <InputText
                  v-model="filters['name']"
                  type="text"
                  class="p-column-filter w-full"
                  placeholder="Nome"
                />
              </template>
            </Column>
            <Column
              field="createdAt"
              :header="$t('date')"
              header-class="sm-invisible text-center"
              body-class="sm-invisible"
              sortable
            >
              <template #filter>
                <div class="sm-invisible">
                  <Calendar
                    v-model="filters['createdAt']"
                    :show-icon="true"
                    placeholder="31/12/1950"
                    date-format="dd/mm/yy"
                    class="w-full onlyIcon"
                    :show-button-bar="true"
                    @date-select="onDateChange"
                  />
                </div>
              </template>
            </Column>
            <Column
              header-class="text-center"
              body-class="text-center"
              :header="$t('files')"
            >
              <template #body="slotProps">
                <PostFile
                  v-if="slotProps.node.data.type !== 'folder'"
                  :file="slotProps.node.data"
                  :display-name="true"
                  class="file-manager-displayed-file d-contents"
                  @show-file="setShowFile(slotProps.node.data)"
                />
              </template>
            </Column>
            <Column :header="$t('actions')" class="text-center">
              <template #body="slotProps">
                <Button
                  v-if="slotProps.node.data.type !== 'folder'"
                  :icon="
                    slotProps.node.data.type &&
                    slotProps.node.data.type === 'folder'
                      ? 'pi pi-plus'
                      : 'pi pi-pencil'
                  "
                  class="p-button p-small"
                  type="button"
                  @click="toggle($event, slotProps.node)"
                />
              </template>
            </Column>
            <template #footer>
              <div class="flex justify-content-end mt-4">
                <Button
                  class="p-button-sm mr-3"
                  icon="pi pi-refresh"
                  @click="getFiles"
                />
                <Button
                  :label="$t('open_folders')"
                  class="p-button-sm mr-3"
                  type="button"
                  icon="pi pi-plus"
                  @click="expandAll"
                />
                <Button
                  :label="$t('close_folders')"
                  type="button"
                  class="p-button-sm"
                  icon="pi pi-minus"
                  @click="collapseAll"
                />
              </div>
            </template>
          </TreeTable>
          <div class="text-center d-none sm-visible">
            Por favor faça scroll horizontal para ver todas as colunas.
          </div>
        </div>
      </div>
    </div>
    <TieredMenu ref="opMenu" :model="items" popup />
    <DialogFiles
      :files="selectedNode ? [selectedNode.data] : []"
      :display-files="showFile"
      @closed="showFile = false"
    />
  </div>
</template>

<script>
import TieredMenu from "primevue/tieredmenu";
import TreeTable from "primevue/treetable";
import PostFile from "../posts/PostFile.vue";
import DialogFiles from "./DialogFiles.vue";

export default {
  components: { TreeTable, PostFile, TieredMenu, DialogFiles },
  data() {
    return {
      files: [],
      isLoading: false,
      expandedKeys: {},
      filters: {
        name: "", // Para o filtro de nome
        createdAt: null, // Para o filtro de data, use null inicialmente
      },
      selectedNode: {},
      showAddFolder: false,
      showAddFiles: false,
      showFile: false,
      folderPath: "",
      items: [
        {
          label: this.$t("view_file"),
          icon: "pi pi-fw pi-search",
          command: () => {
            this.showFile = true;
          },
          visible: () => this.selectedNode.data.type !== "folder",
        },
        {
          label: this.$t("download"),
          icon: "pi pi-fw pi-external-link",
          command: () => {
            this.downloadFile();
          },
          visible: () => this.selectedNode.data.type !== "folder",
        },
      ],
    };
  },
  created() {
    this.getFiles();
  },
  methods: {
    chunkObject(obj, chunkSize) {
      const entries = Object.entries(obj);
      const chunks = [];

      for (let i = 0; i < entries.length; i += chunkSize) {
        const chunk = entries.slice(i, i + chunkSize);
        chunks.push(Object.fromEntries(chunk));
      }

      return chunks;
    },
    getFiles() {
      this.resetVariables();
      this.isLoading = true;

      this.$root
        .apiGet(this.$root.api.processFileUrlsClient)
        .then((response) => {
          const processFileUrls = response.data.processFileUrls || {};
          const chunks = this.chunkObject(processFileUrls, 3);
          const chunkPromises = chunks.map((chunk) =>
            this.$root.apiGet(
              this.$root.getApiClientFiles(this.$root.user.id),
              {
                params: { processFileUrls: JSON.stringify(chunk) },
              }
            )
          );

          return Promise.allSettled(chunkPromises);
        })
        .then((chunkResults) => {
          const fulfilledResponses = chunkResults
            .filter((result) => result.status === "fulfilled")
            .map((result) => result.value);

          // Fallback: se todos os chunks falharem, carrega ao menos a árvore base.
          if (fulfilledResponses.length === 0) {
            return this.$root
              .apiGet(this.$root.getApiClientFiles(this.$root.user.id))
              .then((response) => [response.data.data || []]);
          }

          return fulfilledResponses.map((response) => response.data.data || []);
        })
        .then((trees) => {
          const mergedTree = this.mergeTrees(trees);
          return this.$root
            .apiGet(this.$root.api.posts, {
              params: { dataType: "list", offset: 0 },
            })
            .then((postsResponse) => [mergedTree, postsResponse]);
        })
        .then(([allFiles, postsResponse]) => {
          const myFilesFolder = {
            key: "my-files",
            data: {
              name: "Os meus ficheiros - Mural",
              type: "folder",
              createdAt: this.formatDate(new Date()),
            },
            children: this.processPostFiles(postsResponse.data.data, "Client"),
          };

          const receivedFilesFolder = {
            key: "received-files",
            data: {
              name: "Ficheiros recebidos - Mural",
              type: "folder",
              createdAt: this.formatDate(new Date()),
            },
            children: this.processPostFiles(postsResponse.data.data, "User"),
          };

          this.files = [...allFiles, myFilesFolder, receivedFilesFolder];
          this.isLoading = false;
        })
        .catch((error) => {
          this.isLoading = false;
          this.$root.toastError(
            (error.response &&
              error.response.data &&
              error.response.data.msg_code) ||
              "Erro ao carregar ficheiros"
          );
        });
    },
    mergeTrees(trees) {
      const merged = [];
      trees.forEach((tree) => this.mergeNodes(merged, tree));
      return merged;
    },
    mergeNodes(target, incoming) {
      incoming.forEach((node) => {
        const targetIndex = target.findIndex(
          (existing) => existing.key === node.key
        );
        if (targetIndex === -1) {
          target.push({
            ...node,
            data: node.data ? { ...node.data } : node.data,
            children: Array.isArray(node.children) ? [] : undefined,
          });
          if (Array.isArray(node.children) && node.children.length > 0) {
            this.mergeNodes(target[target.length - 1].children, node.children);
          }
          return;
        }

        const targetNode = target[targetIndex];
        if (Array.isArray(node.children) && node.children.length > 0) {
          if (!Array.isArray(targetNode.children)) {
            targetNode.children = [];
          }
          this.mergeNodes(targetNode.children, node.children);
        }
      });
    },
    processPostFiles(posts, userType) {
      return posts
        .filter((post) => {
          return (
            post.files &&
            post.files.length > 0 &&
            post.user &&
            post.user.type === userType &&
            post.processId === null
          );
        })
        .flatMap((post) => {
          return post.files.map((file) => ({
            key: `post-${post.id}-${file.name}`,
            data: {
              name: file.name,
              type: this.getFileType(file.name),
              url: file.url,
              createdAt: this.formatDate(new Date(post.createdAt)),
              message: post.message,
              user: post.user ? post.user.name : "Sistema",
            },
          }));
        });
    },

    getFileType(fileName) {
      const extension = fileName.split(".").pop().toLowerCase();
      const typeMap = {
        pdf: "pdf",
        doc: "doc",
        docx: "doc",
        xls: "xls",
        xlsx: "xls",
        png: "image",
        jpg: "image",
        jpeg: "image",
        gif: "image",
        csv: "csv",
      };
      return typeMap[extension] || "file";
    },
    expandNode(node) {
      if (node.children && node.children.length) {
        this.expandedKeys[node.key] = true;
        for (let child of node.children) {
          this.expandNode(child);
        }
      }
    },
    expandAll() {
      for (let node of this.files) {
        this.expandNode(node);
      }
      this.expandedKeys = { ...this.expandedKeys };
    },
    collapseAll() {
      this.expandedKeys = {};
    },
    toggle(event, node) {
      this.$refs.opMenu.toggle(event);
      this.selectedNode = node;
      if (this.selectedNode && this.selectedNode.data.path) {
        this.folderPath = this.selectedNode.data.path;
      }
    },
    downloadFile() {
      let url = this.selectedNode.data.url;
      let filename = this.selectedNode.data.name;
      const apiData = this.$root.getDataToDownloadFileApi(url, filename);
      this.$root
        .apiGetFile(apiData.url, apiData.params)
        .then((response) => {
          this.$root.downloadFile(
            response.data,
            response.headers,
            filename,
            url
          );
        })
        .catch((error) => {
          this.$root.toastError(error.response.data.msg_code);
        });
    },
    resetVariables() {
      this.selectedNode = {};
      this.showAddFolder = false;
      this.showAddFiles = false;
    },
    setShowFile(node) {
      this.selectedNode.data = node;
      this.showFile = true;
    },
    onDateChange(value) {
      this.filters["createdAt"] = this.formatDate(value);
    },
    formatDate(date) {
      if (!(date instanceof Date)) return "";

      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    },
  },
};
</script>
